import type { IUpdateParentProfilePayload } from '@/types/payload';

import { enqueueSnackbar } from 'notistack';
import { useRef, Activity, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { IconEdit, IconTrash, IconCamera } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import PhoneNumberInput from '@/components/phoneInput';
import Select from '@/components/Select';
import SettingsSkeleton from '@/components/SkeletonLoader/SettingsSkeleton';
import { useFormatText } from '@/hooks/useFormatText';
import {
  parentProfileSchema,
  type TParentProfileForm,
} from '@/schema/parentProfileSchema';
import {
  uploadImage,
  getParentProfile,
  removeParentChild,
  updateParentProfile,
  getRelationshipDropdown,
} from '@/service/parentSettingsServices';
import { useParentStore } from '@/store/useParentStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import useUser from '@/store/useUser';
import { getAssetUrl } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

interface ILinkedChild {
  id: string;
  name: string;
  age: number;
  profile_pic?: string | null;
}

interface IChildCardProps {
  child: ILinkedChild;
  onDelete: (id: string) => void;
}

interface IRelationshipOption {
  id: number;
  name: string;
}

interface IParentProfileData {
  id?: string;
  name?: string;
  email?: string;
  phone_code?: string;
  phone_number?: string;
  profile_pic?: string | null;
  asset_id?: string;
  relationship?: number | null;
  children?: ILinkedChild[];
}

interface IReadOnlyFieldProps {
  label: string;
  value: string;
}

const ReadOnlyField = ({ label, value }: IReadOnlyFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-sm text-neutral-primary leading-5">{label}</span>
    <div className="bg-white border border-neutral-disabled rounded-lg shadow-[0px_1px_2px_0px_rgba(24,27,37,0.04)] px-3 py-2.5 min-h-11 flex items-center">
      <span className="text-sm text-neutral-primary leading-5 flex-1">
        {value || '—'}
      </span>
    </div>
  </div>
);

const ChildCard = ({ child, onDelete }: IChildCardProps) => {
  const { formatText } = useFormatText();
  const childImage = getAssetUrl(child?.profile_pic);

  return (
    <div className="bg-white border border-neutral-light-200 rounded-lg p-2 flex items-baseline gap-8 h-16 shrink-0">
      <div className="flex items-center gap-2.5">
        {childImage ? (
          <img
            alt={child.name}
            className="w-12 h-12 rounded-full object-cover shrink-0"
            src={childImage}
          />
        ) : (
          <UserAvatarList
            className="w-12! h-12! min-w-12! text-base!"
            user={child.name}
          />
        )}
        <div className="flex flex-col gap-0.5">
          <span className="text-sm text-neutral-primary leading-5">
            {child.name}
          </span>
          <span className="text-sm text-neutral-secondary leading-5">
            {child.age} {formatText('settings.profile.years')}
          </span>
        </div>
      </div>
      <Button
        variant="outline"
        className="text-error-default! hover:text-error-hover transition-colors cursor-pointer shrink-0 bg-neutral-light-100! p-1! border-none!"
        onClick={() => onDelete(child.id)}
        icon={<IconTrash size={16} />}
      />
    </div>
  );
};

const ProfileSettings = () => {
  const { formatText } = useFormatText();
  const { user, setUser } = useUser();
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();
  const { setChildren: setStoreChildren } = useParentStore();

  const [profileData, setProfileData] = useState<IParentProfileData | null>(
    null
  );
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteChildId, setDeleteChildId] = useState<string | null>(null);
  const [relationshipOptions, setRelationshipOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TParentProfileForm>({
    resolver: zodResolver(parentProfileSchema),
    mode: 'onChange',
    defaultValues: {
      fullName: '',
      phone: '',
      countryCode: '+971',
      relationship: '',
    },
  });

  const selectedCountryCode = watch('countryCode');
  const selectedRelationship = watch('relationship');

  const formatRelationshipLabel = (name: string) =>
    name
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

  const mapApiToForm = useCallback(
    (data: IParentProfileData): TParentProfileForm => {
      return {
        fullName: data.name || '',
        phone: data.phone_number || '',
        countryCode: data.phone_code || '+971',
        relationship:
          data.relationship !== null ? String(data.relationship) : '',
      };
    },
    []
  );

  const fetchRelationshipDropdown = useCallback(async () => {
    try {
      const res = await getRelationshipDropdown();
      if (res?.status && Array.isArray(res.data)) {
        const options = (res.data as IRelationshipOption[]).map((item) => ({
          value: String(item.id),
          label: formatRelationshipLabel(item.name),
        }));
        setRelationshipOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch relationship options:', error);
    }
  }, []);

  const fetchParentProfile = useCallback(async () => {
    setScreenLoading(true);
    try {
      const res = await getParentProfile();
      if (res?.status && res?.data) {
        const data = res.data as IParentProfileData;
        setProfileData(data);
        reset(mapApiToForm(data));
        setProfileImage(getAssetUrl(data?.profile_pic));
        setAssetId(data.asset_id || '');
        setUser({
          ...user,
          name: data.name || user.name,
          profile_pic: getAssetUrl(data?.profile_pic),
        });
      }
    } catch (error) {
      console.error('Failed to fetch parent profile:', error);
    } finally {
      setScreenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapApiToForm, reset]);

  useEffect(() => {
    fetchRelationshipDropdown();
    fetchParentProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleEdit = useCallback(() => {
    reset(mapApiToForm(profileData || {}));
    setProfileImage(getAssetUrl(profileData?.profile_pic));
    setIsEditing(true);
  }, [mapApiToForm, profileData, reset]);

  const handleCancel = useCallback(() => {
    reset(mapApiToForm(profileData || {}));
    setProfileImage(getAssetUrl(profileData?.profile_pic));
    setAssetId(profileData?.asset_id || '');
    setIsEditing(false);
  }, [mapApiToForm, profileData, reset]);

  const onSubmit = useCallback(
    async (data: TParentProfileForm) => {
      try {
        const payload: IUpdateParentProfilePayload = {
          name: data.fullName,
          phone_code: data.countryCode || '+971',
          phone_number: data.phone,
          ...(assetId && { asset_id: assetId }),
          ...(data.relationship && {
            relationship: Number(data.relationship),
          }),
        };

        const response = await updateParentProfile(payload);

        if (response?.status) {
          setUser({
            ...user,
            name: payload.name,
            profile_pic: profileImage || '',
          });
          setIsEditing(false);
          await fetchParentProfile();
        }
      } catch (error) {
        console.error('Failed to update parent profile:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetId, fetchParentProfile]
  );

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please upload an image file', { variant: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('Image size should be less than 5MB', {
          variant: 'error',
        });
        return;
      }

      // Show local preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to server
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'teacher');
        const response = await uploadImage(formData);
        if (response?.status && response?.data?.id) {
          setAssetId(response.data.id);
        } else {
          setProfileImage(getAssetUrl(profileData?.profile_pic));
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        setProfileImage(getAssetUrl(profileData?.profile_pic));
      } finally {
        setIsUploading(false);
        // Reset file input so the same file can be re-selected
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [profileData?.profile_pic]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDeleteChild = useCallback((id: string) => {
    setDeleteChildId(id);
  }, []);

  const confirmDeleteChild = useCallback(async () => {
    if (!deleteChildId) return;

    const response = await removeParentChild(deleteChildId);
    if (response?.status) {
      const updatedChildren =
        profileData?.children?.filter((c) => c.id !== deleteChildId) ?? [];
      setProfileData((prev) => {
        if (!prev) return prev;
        return { ...prev, children: updatedChildren };
      });

      setStoreChildren(
        updatedChildren?.map((c) => ({
          id: c?.id,
          name: c?.name,
          age: c?.age,
          profile_pic: getAssetUrl(c?.profile_pic) || null,
        }))
      );
      setDeleteChildId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteChildId, profileData?.children]);

  const childToDelete = profileData?.children?.find(
    (c) => c.id === deleteChildId
  );

  if (isScreenLoading) {
    return <SettingsSkeleton />;
  }

  return (
    <div className="max-w-217 w-full">
      {/* Page Title */}
      <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
        {formatText('settings.profile.title')}
      </h2>

      {/* Card */}
      <div className="mt-6 border border-neutral-light-200 rounded-2xl p-6 flex flex-col gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Profile Information ─────────────────────────────────────── */}
          <div className="flex flex-col gap-4">
            {/* Header row */}
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-neutral-primary leading-6">
                {formatText('settings.profile.profile-information')}
              </h3>

              {/* Edit / Save + Cancel */}
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <>
                    <Button
                      className="px-4! py-2! text-sm!"
                      label={formatText('common.actions.cancel')}
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                    />
                    <Button
                      className="px-4! py-2! text-sm!"
                      label={formatText('common.actions.save-changes')}
                      loading={isSubmitting || isUploading}
                      type="submit"
                      variant="primary"
                    />
                  </>
                ) : (
                  <Button
                    className="p-2.5 rounded-lg border border-neutral-light-200! bg-white! shadow-[0px_1px_2px_0px_rgba(24,27,37,0.04)] hover:bg-neutral-light-50! transition-colors cursor-pointer"
                    type="button"
                    onClick={handleEdit}
                    icon={
                      <IconEdit className="text-neutral-secondary" size={20} />
                    }
                  />
                )}
              </div>
            </div>

            {/* Content area with bottom divider */}
            <div className="border-b border-neutral-light-200 pb-6 flex flex-col gap-6">
              {/* Avatar */}
              <div className="relative w-16 h-16 group">
                {profileImage ? (
                  <img
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover"
                    src={profileImage}
                  />
                ) : (
                  <UserAvatarList
                    className="w-16! h-16! min-w-16! text-xl!"
                    user={
                      isEditing
                        ? watch('fullName') || profileData?.name || ''
                        : profileData?.name || ''
                    }
                  />
                )}

                {isEditing && (
                  <>
                    <Button
                      variant="unstyled"
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      disabled={isUploading}
                      onClick={handleUploadClick}
                      icon={<IconCamera className="text-white" size={24} />}
                    />
                    <input
                      ref={fileInputRef}
                      accept="image/*"
                      className="hidden"
                      type="file"
                      onChange={handleImageUpload}
                    />
                  </>
                )}

                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Row 1: Full Name | Email ID */}
              <div className="flex gap-10 items-start w-full">
                <div className="flex-1 max-w-87.5 min-h-15">
                  {isEditing ? (
                    <Input
                      autoFocus
                      error={
                        errors.fullName?.message
                          ? formatText(errors.fullName.message)
                          : undefined
                      }
                      label={formatText('settings.profile.full-name')}
                      name="fullName"
                      inputWrapperClassName="border-neutral-disabled!"
                      placeholder={formatText(
                        'settings.profile.full-name-placeholder'
                      )}
                      register={register}
                      required
                    />
                  ) : (
                    <ReadOnlyField
                      label={formatText('settings.profile.full-name')}
                      value={profileData?.name || ''}
                    />
                  )}
                </div>

                <div className="flex-1 max-w-87.5 min-h-15">
                  {/* Email is always read-only */}
                  <ReadOnlyField
                    label={formatText('settings.profile.email-id')}
                    value={profileData?.email || ''}
                  />
                </div>
              </div>

              {/* Row 2: Phone Number | Relationship */}
              <div className="flex gap-10 items-start w-full">
                <div className="flex-1 max-w-87.5 min-h-15">
                  {isEditing ? (
                    <PhoneNumberInput
                      countryFlag
                      countrySelected={selectedCountryCode ?? '+971'}
                      error={
                        errors.phone?.message
                          ? formatText(errors.phone.message)
                          : undefined
                      }
                      label={formatText('settings.profile.phone-number')}
                      name="phone"
                      placeholder={formatText(
                        'settings.profile.phone-number-placeholder'
                      )}
                      register={register}
                      required
                      setValue={setValue}
                    />
                  ) : (
                    <ReadOnlyField
                      label={formatText('settings.profile.phone-number')}
                      value={`${profileData?.phone_code || ''} ${profileData?.phone_number || ''}`.trim()}
                    />
                  )}
                </div>

                <div className="flex-1 max-w-87.5 min-h-15">
                  {isEditing ? (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-sm text-neutral-primary leading-5">
                        {formatText('settings.profile.relationship')}
                      </span>
                      <Select
                        options={relationshipOptions}
                        placeholder={formatText(
                          'settings.profile.select-relationship'
                        )}
                        className="py-3 border-neutral-disabled"
                        selected={selectedRelationship || ''}
                        onChange={(val) =>
                          setValue('relationship', val as string, {
                            shouldValidate: true,
                          })
                        }
                      />
                      {errors.relationship?.message && (
                        <span className="text-xs text-error-default">
                          {formatText(errors.relationship.message)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <ReadOnlyField
                      label={formatText('settings.profile.relationship')}
                      value={
                        relationshipOptions?.find(
                          (o) => o?.value === selectedRelationship
                        )?.label || ''
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* ── Linked Children ──────────────────────────────────────────── */}
        <div className="flex flex-col gap-4">
          <h3 className="text-base font-semibold text-neutral-primary leading-6">
            {formatText('settings.profile.linked-children')}
          </h3>

          {profileData?.children && profileData.children.length > 0 ? (
            <div className="flex flex-wrap gap-6">
              {profileData.children.map((child) => (
                <ChildCard
                  key={child.id}
                  child={child}
                  onDelete={handleDeleteChild}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-secondary leading-5">—</p>
          )}
        </div>
      </div>

      {/* Delete Child Confirmation */}
      <Activity mode={deleteChildId ? 'visible' : 'hidden'}>
        <ConfirmationModal
          confirmLabel={formatText('common.actions.delete')}
          descriptionPrefix={'common.delete-confirmation.prefix'}
          descriptionSuffix={'common.delete-confirmation.suffix'}
          entityName={`${childToDelete?.name}?`}
          isDestructive
          isOpen={!!deleteChildId}
          setIsOpen={(open) => !open && setDeleteChildId(null)}
          title={'common.delete-confirmation.title'}
          onConfirm={confirmDeleteChild}
        />
      </Activity>
    </div>
  );
};

export default ProfileSettings;
